/*
 * <heritage-account-memos>
 *
 * The partner's pending Memo requests, read from window.MDJMemos and
 * re-rendered on `mdj:memos-changed`. Each memo request is a whole-cart
 * submission, so the collapsed card shows aggregate info (piece count +
 * total) plus a thumbnail strip of up to three included pieces.
 *
 * Each card is an expandable <details>. Expanding reveals the full
 * itemized request — every piece, the note, totals, and a "Submitted
 * by" block (store, contact, ship-to address) — so that, worst case,
 * the partner can screenshot it and send it to MDJ manually. The
 * expanded panel also carries the actions:
 *
 *   Resubmit to MDJ — re-fires the notification through the `memo`
 *     webhook (MDJMemos.resubmit) using the partner's current contact
 *     context. This exists because the legacy contact-form email could
 *     be silently dropped by Shopify's CAPTCHA/spam protection, leaving
 *     a request the partner thinks they sent but MDJ never received.
 *     The button shows a real "Resent ✓" / "couldn't reach MDJ" state;
 *     on failure it reveals the screenshot fallback hint.
 *
 *   Cancel request — removes locally + fires the cancel webhook
 *     (unchanged behaviour).
 *
 * Storage is per-device localStorage, so a memo only appears on the
 * device it was submitted from (see heritage-memo.js). The contact
 * shown in "Submitted by" is the currently signed-in partner, resolved
 * from window.__MDJ_INTEGRATIONS__ — submit-time contact isn't persisted
 * locally, and the current account is the right party for MDJ to act on.
 *
 * Markup contract:
 *   <heritage-account-memos data-limit="3">
 *     <div data-grid hidden></div>
 *     <div data-empty>(server-rendered empty state)</div>
 *   </heritage-account-memos>
 */

class HeritageAccountMemos extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.handleCancelClick = this.handleCancelClick.bind(this);
    this.handleResubmitClick = this.handleResubmitClick.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
  }

  connectedCallback() {
    this.limit = parseInt(this.getAttribute('data-limit') || '3', 10) || 3;
    this.gridEl = this.querySelector('[data-grid]');
    this.emptyEl = this.querySelector('[data-empty]');
    // Expand state survives re-renders (add/cancel rewrite the grid).
    this.openIds = new Set();
    this.contact = this.customerContext();
    this.render();
    document.addEventListener('mdj:memos-changed', this.onChange);
    this.addEventListener('click', this.handleCancelClick);
    this.addEventListener('click', this.handleResubmitClick);
    // `toggle` doesn't bubble — capture it on the host instead.
    this.addEventListener('toggle', this.handleToggle, true);
  }

  disconnectedCallback() {
    document.removeEventListener('mdj:memos-changed', this.onChange);
    this.removeEventListener('click', this.handleCancelClick);
    this.removeEventListener('click', this.handleResubmitClick);
    this.removeEventListener('toggle', this.handleToggle, true);
  }

  onChange() {
    this.render();
  }

  handleToggle(event) {
    const card = event.target;
    if (!card || !card.matches || !card.matches('[data-memo-card]')) return;
    const id = card.dataset.memoId;
    if (!id) return;
    if (card.open) this.openIds.add(id);
    else this.openIds.delete(id);
  }

  handleCancelClick(event) {
    const btn = event.target.closest && event.target.closest('[data-memo-cancel]');
    if (!btn) return;
    event.preventDefault();
    const id = btn.dataset.memoId;
    if (!id || !window.MDJMemos) return;
    if (!confirm('Cancel this memo request? MDJ will be notified.')) return;
    window.MDJMemos.remove(id, this.gatherContext());
  }

  async handleResubmitClick(event) {
    const btn = event.target.closest && event.target.closest('[data-memo-resubmit]');
    if (!btn) return;
    event.preventDefault();
    const id = btn.dataset.memoId;
    if (!id || !window.MDJMemos || !window.MDJMemos.resubmit) return;
    if (btn.dataset.busy === 'true') return;

    const card = btn.closest('[data-memo-card]');
    const hint = card ? card.querySelector('[data-memo-fallback-hint]') : null;
    const original = btn.dataset.label || btn.textContent;
    btn.dataset.label = original;
    btn.dataset.busy = 'true';
    btn.disabled = true;
    btn.textContent = 'Sending…';
    if (hint) hint.hidden = true;

    let ok = false;
    try {
      ok = await window.MDJMemos.resubmit(id, this.resubmitContext());
    } catch (e) {
      ok = false;
    }

    if (ok) {
      btn.textContent = 'Resent ✓';
      btn.classList.add('is-sent');
      window.setTimeout(() => {
        btn.textContent = original;
        btn.classList.remove('is-sent');
        btn.disabled = false;
        btn.dataset.busy = 'false';
      }, 4000);
    } else {
      // Most likely the webhook endpoint isn't configured yet — point
      // the partner at the screenshot fallback rather than fail silently.
      btn.textContent = original;
      btn.classList.remove('is-sent');
      btn.disabled = false;
      btn.dataset.busy = 'false';
      if (hint) hint.hidden = false;
    }
  }

  // Base contact context for the signed-in partner — store name, email,
  // phone, and ship-to address on file. Resolved from the integrations
  // blob, same source the memo modal submits from.
  customerContext() {
    const cfg = window.__MDJ_INTEGRATIONS__ || {};
    const c = cfg.customer || {};
    const a = c.default_address || {};
    const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
    return {
      customer_id: c.id || null,
      email: c.email || '',
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      name: name || c.email || '',
      phone: a.phone || c.phone || '',
      company: a.company || '',
      address: {
        address1: a.address1 || '',
        address2: a.address2 || '',
        city: a.city || '',
        province: a.province || '',
        province_code: a.province_code || '',
        zip: a.zip || '',
        country: a.country || '',
        country_code: a.country_code || ''
      },
      tags: c.tags || ''
    };
  }

  gatherContext() {
    return Object.assign(this.customerContext(), {
      cancelled_at: new Date().toISOString(),
      cancelled_at_local: new Date().toLocaleString()
    });
  }

  resubmitContext() {
    return Object.assign(this.customerContext(), {
      resubmitted_at: new Date().toISOString(),
      resubmitted_at_local: new Date().toLocaleString()
    });
  }

  render() {
    if (!window.MDJMemos || !this.gridEl) return;
    const items = window.MDJMemos.getAll().slice(0, this.limit);

    if (items.length === 0) {
      this.gridEl.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = false;
      // Drop stale expand state for memos that no longer exist.
      this.openIds.clear();
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    this.gridEl.hidden = false;
    this.gridEl.innerHTML = items.map((m) => this.renderCard(m)).join('');
  }

  renderCard(memo) {
    const id = this.safe(String(memo.id || ''));
    const items = Array.isArray(memo.items) ? memo.items : [];
    const count = items.length;
    const total = this.formatMoney(memo.total_price);
    const requested = memo.requestedAt ? new Date(memo.requestedAt).toLocaleDateString() : '';
    const status = memo.status === 'completed' ? 'Fulfilled' : 'Pending';

    const thumbs = items.slice(0, 3).map((it) => {
      const img = this.safe(it.image || '');
      return '<span class="heritage-account__list-card-thumb"' + (img ? ' style="background-image: url(\'' + img + '\')"' : '') + '></span>';
    }).join('');
    const extraCount = count > 3 ? '<span class="heritage-account__list-card-thumb heritage-account__list-card-thumb--more">+' + (count - 3) + '</span>' : '';

    const firstTitle = items[0] ? this.safe(items[0].title || '') : '';
    const label = count > 1
      ? firstTitle + ' <span class="heritage-account__list-card-and">+ ' + (count - 1) + ' more</span>'
      : firstTitle;

    const open = this.openIds.has(memo.id) ? ' open' : '';

    return (
      '<details class="heritage-account__memo" data-memo-card data-memo-id="' + id + '"' + open + '>' +
        '<summary class="heritage-account__memo-summary">' +
          '<span class="heritage-account__list-card-thumbs">' + thumbs + extraCount + '</span>' +
          '<span class="heritage-account__list-card-body">' +
            '<span class="heritage-account__list-card-era">' + count + ' piece' + (count === 1 ? '' : 's') + (total ? ' · ' + this.safe(total) : '') + '</span>' +
            '<span class="heritage-account__list-card-name">' + label + '</span>' +
            '<span class="heritage-account__list-card-meta">' + status + (requested ? ' · Requested ' + requested : '') + '</span>' +
          '</span>' +
          '<span class="heritage-account__memo-chevron" aria-hidden="true">' +
            '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 6l4 4 4-4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</span>' +
        '</summary>' +
        this.renderDetail(memo, id, count) +
      '</details>'
    );
  }

  renderDetail(memo, id, count) {
    const items = Array.isArray(memo.items) ? memo.items : [];

    const lines = items.map((it) => {
      const img = this.safe(it.image || '');
      const rawSku = it.sku || '';
      const sku = window.MDJFormatSku ? window.MDJFormatSku(rawSku) : rawSku;
      const meta = [];
      if (sku) meta.push('No. ' + this.safe(sku));
      meta.push('Qty ' + (it.qty || 1));
      return (
        '<li class="heritage-account__memo-line">' +
          '<span class="heritage-account__memo-line-thumb"' + (img ? ' style="background-image: url(\'' + img + '\')"' : '') + '></span>' +
          '<span class="heritage-account__memo-line-info">' +
            '<span class="heritage-account__memo-line-title">' + this.safe(it.title || '(untitled)') + '</span>' +
            '<span class="heritage-account__memo-line-meta">' + meta.join(' · ') + '</span>' +
          '</span>' +
          '<span class="heritage-account__memo-line-price">' + this.safe(this.formatMoney(it.line_price)) + '</span>' +
        '</li>'
      );
    }).join('');

    const noteBlock = (memo.note && String(memo.note).trim())
      ? '<div class="heritage-account__memo-note">' +
          '<span class="heritage-account__memo-detail-label">Note</span>' +
          '<p>' + this.safe(memo.note) + '</p>' +
        '</div>'
      : '';

    const total = this.formatMoney(memo.total_price);
    const totals = (
      '<div class="heritage-account__memo-totals">' +
        '<span>' + count + ' piece' + (count === 1 ? '' : 's') + '</span>' +
        '<span>' + this.safe(total) + '</span>' +
      '</div>'
    );

    const c = this.contact || {};
    const subRows = [
      ['Store', c.company],
      ['Name', c.name],
      ['Email', c.email],
      ['Phone', c.phone],
      ['Ship to', this.formatAddress(c.address)]
    ].filter((r) => r[1] && String(r[1]).trim())
      .map((r) => '<div class="heritage-account__memo-sub-row"><dt>' + this.safe(r[0]) + '</dt><dd>' + this.safe(r[1]) + '</dd></div>')
      .join('');
    const submitter = subRows
      ? '<div class="heritage-account__memo-submitter">' +
          '<span class="heritage-account__memo-detail-label">Submitted by</span>' +
          '<dl class="heritage-account__memo-sub-list">' + subRows + '</dl>' +
        '</div>'
      : '';

    return (
      '<div class="heritage-account__memo-detail">' +
        '<ul class="heritage-account__memo-lines">' + lines + '</ul>' +
        noteBlock +
        totals +
        submitter +
        '<div class="heritage-account__memo-actions">' +
          '<button type="button" class="heritage-account__memo-btn heritage-account__memo-btn--primary" data-memo-resubmit data-memo-id="' + id + '">Resubmit request</button>' +
          '<button type="button" class="heritage-account__memo-btn" data-memo-cancel data-memo-id="' + id + '">Cancel request</button>' +
        '</div>' +
        '<p class="heritage-account__memo-fallback-hint" data-memo-fallback-hint hidden>' +
          'Couldn’t send automatically — screenshot the details above and send them to your rep to get it fulfilled.' +
        '</p>' +
      '</div>'
    );
  }

  formatAddress(a) {
    if (!a) return '';
    const line1 = [a.address1, a.address2].filter(Boolean).join(' ');
    const cityRegion = [a.city, a.province_code || a.province].filter(Boolean).join(', ');
    const tail = [cityRegion, a.zip].filter(Boolean).join(' ');
    const country = a.country || a.country_code || '';
    return [line1, tail, country].filter(Boolean).join(', ');
  }

  formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    return '$' + (cents / 100).toFixed(2);
  }

  safe(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

if (!customElements.get('heritage-account-memos')) {
  customElements.define('heritage-account-memos', HeritageAccountMemos);
}
